/* eslint-disable react/prop-types */
import React, {
  useEffect,
  useMemo,
  useCallback,
  useState,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import ResizeObserver from 'rc-resize-observer';
import { VariableSizeGrid as Grid } from 'react-window';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import axios from 'axios';

import {
  Input,
  Table,
  Row,
  Col,
  Spin,
} from 'antd';
import { DownloadOutlined, FilterFilled, FilterOutlined } from '@ant-design/icons';

import { getFormLogColumns } from '../FormColumns';
import FormResponder from '../FormResponder';

import {
  getForms,
  getFormById,
  getFormApprovalSummary,
  updateFormFilters,
  closeForm,
} from '../state/forms.actions';

import FormDetailView from './FormDetailView';
import FormFilterDrawer from './FormFilterDrawer';
import InvoiceAddDrawer from '../../payables/invoices/InvoiceAddDrawer';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import TimeCardDatePicker from '../../timecards/TimeCardDatePicker';

import useFormSearch from '../../common/hooks/useFormSearch';

import {
  arrayIncludesTerm,
  getIdMap,
  includesTerm,
  isNullOrUndefined,
  toTitleCase,
} from '../../helpers/helpers';
import Analytics from '../../helpers/Analytics';
import { INVOICE_DRAWER_ADD_MODE } from '../../payables/invoices/invoiceConstants';
import ReportExcelExport from '../../reports/reportExcelExport';
import { parseFormResponseToReadableFormat } from '../ResponderHelpers';
import { textBasedFieldTypes, handleFormClick, getFormListAndFilters } from '../formHelpers';
import { request } from '../../helpers/requests';
import { getUserIdMap } from '../../state/selectors';

const exporter = new ReportExcelExport();

const ROW_HEIGHT = 61;

function FormsList({
  topOffset = 220,
  forms = [],
  hideDate,
  shouldApplyRange = false,
  query,
  visible = true,
  range,
  onRangeChange,
  isPayable,
  option,
  hideWhenEmpty,
  enableFormFilters,
  isFilterActive,
  customSearch,
  isPO,
  skipLoad,
  parentLoading,
  shouldGetTemplates,
  onCardColumnClick,
  onAnalyticsClick,
  hideExportButton = false,
  registerFormHeaderAction,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const formApprovals = useSelector((state) => state.forms.approvalSummary);
  const customers = useSelector((state) => state.customers.customers);
  const projects = useSelector((state) => state.projects.projects);
  const vendorMap = useSelector((state) => state.vendors.vendors);
  const { settings } = useSelector((state) => state.settings.company);
  const assignedFormTemplates = useSelector((state) => state.forms.assignedFormTemplates);
  const userMap = useSelector(getUserIdMap);

  const gridRef = useRef();
  const tableDimensionsRef = useRef({ width: 1000, height: 1000 });

  const [searchTerm, setSearchTerm] = useState();
  const [showDetail, setShowDetail] = useState(false);
  const [poCloseMode, setPoCloseMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [invDrawerVisible, setInvDrawerVisible] = useState(false);
  const [invDrawerMode, setInvDrawerMode] = useState(INVOICE_DRAWER_ADD_MODE);
  const [selectedFormId, setSelectedFormId] = useState('');
  const [tableDimensions, setTableDimensions] = useState({ width: 1000, height: 1000 });
  const [filteredRows, setFilteredRows] = useState();
  const [filters, setFilters] = useState();
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [showResponder, setShowResponder] = useState(false);
  const [calendarType, setCalendarType] = useState('payPeriod');

  const [connectObject] = useState(() => {
    const obj = {
      scrollTo: gridRef.scrollTo,
    };
    Object.defineProperty(obj, 'scrollLeft', {
      get: () => {
        if (gridRef.current) {
          return gridRef.current?.state?.scrollLeft;
        }
        return null;
      },
      set: (scrollLeft) => {
        if (gridRef.current) {
          gridRef.current.scrollTo({
            scrollLeft,
          });
        }
      },
    });

    Object.defineProperty(obj, 'scrollTop', {
      get: () => {
        if (gridRef.current) {
          return gridRef.current?.state?.scrollTop;
        }
        return null;
      },
      set: (scrollTop) => {
        if (gridRef.current) {
          gridRef.current.scrollTo({
            scrollTop,
          });
        }
      },
    });
    return obj;
  });

  const onCopy = useCallback((record) => {
    handleFormClick({
      record,
      dispatch,
      loading,
      setLoading,
      setShowDetail: setShowResponder,
    });
  }, []);

  const clearFormSelection = useCallback(() => {
    setShowDetail(false);
    setShowResponder(false);
  }, []);
  const updateInvDrawerMode = useCallback(() => setInvDrawerMode(INVOICE_DRAWER_ADD_MODE), []);
  const closeDrawer = useCallback(() => setInvDrawerVisible(false), []);
  const onAddInvoiceClick = useCallback((formId) => {
    setSelectedFormId(formId);
    setInvDrawerVisible(true);
    setInvDrawerMode(INVOICE_DRAWER_ADD_MODE);
  }, []);

  const onPOClose = useCallback(async (formId) => {
    setLoading(true);
    if (await dispatch(getFormById(formId, { getRecentDraft: true }))) {
      setShowDetail(true);
      setPoCloseMode(true);
    }
    setLoading(false);
  }, [loading]);

  const onDirectPOClose = useCallback(async (formId, formValue) => (
    dispatch(closeForm(formId, formValue))
  ), []);

  const assignedTemplateSet = useMemo(() => (
    new Set(assignedFormTemplates.map((template) => template.id))
  ), [assignedFormTemplates]);

  const includeRangeInQuery = !hideDate || shouldApplyRange;

  const searchParams = useMemo(() => {
    const [
      startTime = DateTime.local().minus({ days: 14 }).toMillis(),
      endTime = DateTime.local().toMillis(),
    ] = range ?? [];
    return {
      ...(query || {}),
      searchTerm,
      ...(includeRangeInQuery ? { startTime, endTime } : {}),
      includePreloaded: isPO,
    };
  }, [query, searchTerm, includeRangeInQuery, range, isPO]);

  const {
    searchSet,
    searchLoading,
  } = useFormSearch(searchParams);

  const resetVirtualGrid = useCallback(() => {
    gridRef.current?.resetAfterIndices({
      columnIndex: 0,
      shouldForceUpdate: true,
    });
  }, []);

  const onTableResize = useCallback(({ height, width }) => {
    const next = { height: Math.round(height), width: Math.round(width) };
    const prev = tableDimensionsRef.current;
    if (prev.height === next.height && prev.width === next.width) return;
    tableDimensionsRef.current = next;
    setTableDimensions(next);
    if (prev.width !== next.width) {
      resetVirtualGrid();
    }
  }, [resetVirtualGrid]);

  const onSearch = useCallback(async (e) => {
    const {
      target: {
        value,
      } = {},
    } = e;
    setSearchTerm(value);
  }, []);

  const onFormClick = useCallback(async (record) => {
    if (loading) return;
    Analytics.track('Forms/ViewSubmitted');
    setLoading(true);
    if (await dispatch(getFormById(record.id, { getRecentDraft: true }))) {
      setShowDetail(true);
    }
    setLoading(false);
  }, [loading]);

  const projectIdMap = useMemo(() => getIdMap(projects), [projects]);

  const {
    formList,
    vendorFilters,
    customerFilters,
    projectFilters,
    userFilters,
    templateFilters,
    statusFilters,
    cardFilters,
  } = useMemo(() => getFormListAndFilters({
    forms,
    isPayable,
    userMap,
    vendorMap,
    projectIdMap,
    customers,
    hideDate,
    shouldApplyRange,
    range,
  }), [forms, isPayable, userMap, vendorMap, projectIdMap, customers, hideDate, shouldApplyRange, range]);

  const columns = useMemo(() => (
    getFormLogColumns({
      userMap,
      vendorMap,
      projectIdMap,
      formApprovals,
      templateFilters,
      statusFilters,
      userFilters,
      vendorFilters,
      customerFilters,
      projectFilters,
      cardFilters,
      query,
      isPayable,
      onAddInvoiceClick,
      onPOClose,
      onDirectPOClose,
      settings,
      t,
      isPO,
      onCopy,
      assignedTemplateSet,
      onCardColumnClick,
      onFormClick,
    })
  ), [
    userMap,
    vendorMap,
    projectIdMap,
    formApprovals,
    templateFilters,
    statusFilters,
    userFilters,
    vendorFilters,
    customerFilters,
    projectFilters,
    cardFilters,
    query,
    onAddInvoiceClick,
    onPOClose,
    onDirectPOClose,
    isPayable,
    isPO,
    onCopy,
    assignedTemplateSet,
    onCardColumnClick,
    onFormClick,
    settings,
  ]);

  const decoratedColumns = useMemo(() => {
    const columnWidthSum = columns.reduce((acc, col) => acc + col.width, 0);
    const { width: tableWidth } = tableDimensions;
    return columns.map((col) => {
      const { width } = col;
      if (columnWidthSum === 0) return col;
      const expected = tableWidth * (width / columnWidthSum);
      return {
        ...col,
        width: expected > width ? expected : width,
      };
    });
  }, [tableDimensions, columns]);

  useEffect(() => {
    const load = async () => {
      const [
        startTime = DateTime.local().minus({ days: 14 }).toMillis(),
        endTime = DateTime.local().toMillis(),
      ] = range ?? [];
      setLoading(true);
      await Promise.all([
        dispatch(getForms({
          ...(query || {}),
          ...(includeRangeInQuery ? { startTime, endTime } : {}),
          includePreloaded: isPO,
        })),
        dispatch(getFormApprovalSummary()),
      ]);
      setLoading(false);
    };
    const shouldLoad = visible && !skipLoad && (
      includeRangeInQuery ? (range && range.length === 2) : true
    );
    if (shouldLoad) {
      load();
    }

    if (!visible) {
      setFilteredRows();
    }
  }, [hideDate, shouldApplyRange, includeRangeInQuery, query, range, visible, skipLoad, isPO]);

  const tableData = useMemo(() => (
    !searchSet
      ? formList
      : formList.filter(({
        status,
        number,
        templateName,
        userId,
        id: formId,
        projectNames,
        customer,
        formValue,
        costToDate,
        userNameList = new Set(),
        vendorNameList = new Set(),
      }) => (
        !searchTerm
          || (!isNullOrUndefined(status) && includesTerm(status, searchTerm))
          || (!isNullOrUndefined(number) && includesTerm(number, searchTerm))
          || (!isNullOrUndefined(templateName) && includesTerm(templateName, searchTerm))
          || (userId in userMap && includesTerm(userMap[userId].name, searchTerm))
          || searchSet.has(formId)
          || (!isNullOrUndefined(projectNames) && includesTerm(projectNames, searchTerm))
          || (!isNullOrUndefined(customer) && includesTerm(customer, searchTerm))
          || (!isNullOrUndefined(formValue) && typeof formValue === 'number' && includesTerm(formValue.toString(), searchTerm))
          || (!isNullOrUndefined(costToDate) && typeof costToDate === 'number' && includesTerm(costToDate.toString(), searchTerm))
          || (userNameList.size > 0 && arrayIncludesTerm(Array.from(userNameList), searchTerm))
          || (vendorNameList.size > 0 && arrayIncludesTerm(Array.from(vendorNameList), searchTerm))
      ))
  ), [searchSet, formList, searchTerm]);

  const height = `calc(100vh - ${topOffset}px)`;
  const renderVirtualList = useCallback((rawData, { scrollbarSize, ref, onScroll }) => {
    ref.current = connectObject;
    const { height: tableHeight, width: tableWidth } = tableDimensionsRef.current;
    const totalHeight = rawData.length * ROW_HEIGHT;
    return (
      <Grid
        ref={gridRef}
        className="virtual-grid"
        columnCount={decoratedColumns.length}
        columnWidth={(index) => {
          const { width } = decoratedColumns[index];
          return totalHeight > tableHeight && index === decoratedColumns.length - 1
            ? width - scrollbarSize - 1
            : width;
        }}
        height={tableHeight - 39}
        rowCount={rawData.length}
        rowHeight={() => ROW_HEIGHT}
        width={tableWidth}
        onScroll={({ scrollLeft }) => {
          onScroll({
            scrollLeft,
          });
        }}
      >
        {({ columnIndex, rowIndex, style }) => {
          const column = decoratedColumns[columnIndex];
          const record = rawData[rowIndex] ?? {};
          const value = record[column.dataIndex];
          const body = column.render
            ? column.render(value, record)
            : value;
          return (
            <div
              onClick={() => onFormClick(record)}
              className={
                columnIndex === decoratedColumns.length - 1
                  ? 'virtual-table-cell-base'
                  : 'virtual-table-cell'
              }
              style={style}
            >
              <div className="virtual-table-cell-inner">
                {body}
              </div>
            </div>
          );
        }}
      </Grid>
    );
  }, [connectObject, onFormClick, decoratedColumns]);

  const tableComponents = useMemo(() => ({
    body: renderVirtualList,
  }), [renderVirtualList]);

  const onExport = useCallback(async () => {
    setExportLoading(true);

    const existingTemplateNames = new Set();
    // Convert filter arrays to Sets for O(1) lookups
    const templateNameSet = filters?.templateName ? new Set(filters.templateName) : null;
    const projectNameSet = filters?.projectNames ? new Set(filters.projectNames) : null;
    const projectNumberSet = filters?.projectNumbers ? new Set(filters.projectNumbers) : null;
    const customerSet = filters?.customer ? new Set(filters.customer) : null;
    const cardSet = filters?.card ? new Set(filters.card) : null;
    const userIdSet = filters?.userId ? new Set(filters.userId) : null;
    // Pre-convert statusFilters to Map for O(1) lookups
    const statusMap = new Map(statusFilters.map((s) => [s.value, s.text]));
    // Convert status filter to Set of status strings
    // have to do this filters.status has its own status ids that correspond to statusFilters
    const statusSet = filters?.status ? new Set(
      filters?.status.map((statusId) => toTitleCase(statusMap.get(statusId) || '')),
    ) : null;
    const dataSource = filteredRows && filteredRows.length > 0
      ? tableData.filter((form) => (filters.templateName === null || templateNameSet.has(form.templateName))
        && (filters.projectNames == null || form.projects?.some((project) => projectNameSet.has(project)))
        && (filters.projectNumbers == null || form.projects?.some((project) => projectNumberSet.has(project)))
        && (filters.customer === null || form.customers?.some((customer) => customerSet.has(customer)))
        && (filters.status === null || statusSet.has(toTitleCase(form.status)))
        && (filters.card === null || cardSet.has(form.card))
        && (filters.userId === null || form.users?.some((userId) => userIdSet.has(userId))))
      : tableData;
    const numRows = dataSource.length;
    let exportMaps = {};
    if (numRows <= 1000) {
      const {
        data: eMaps = {},
      } = await request({
        call: axios.post('/forms/exports/data', {
          ids: dataSource.map((row) => row.id),
        }),
        errMsg: 'Failed to get form fields',
        hideSuccessToast: true,
      });
      exportMaps = eMaps;
    }
    const groupedForms = dataSource.reduce((acc, form) => {
      const {
        templateId,
        templateName,
        projectNames,
      } = form;

      const {
        formMap: {
          [form.id]: data = {},
        } = {},
        templateMap: {
          [form.templateId]: templateSchema = {},
        } = {},
      } = exportMaps ?? {};

      if (!acc[templateId]) {
        const relevantColumns = columns.map((column) => ({
          ...column,
          field: column.exportDataIndex || column.dataIndex,
        })).filter((column) => !!column.title);

        const fieldTitleMap = {}; // for most recent work date
        templateSchema?.sections?.forEach(({ fields }) => {
          fields.forEach((field) => {
            const {
              configProps = {},
              selectedType,
              id: fieldId,
            } = field;

            if (selectedType === 'table' && configProps?.dataType === 'TimeEntry') {
              fieldTitleMap[fieldId] = configProps.title;
              return;
            }

            if (textBasedFieldTypes.has(selectedType)) {
              relevantColumns.push({
                title: configProps?.title,
                field: fieldId,
              });
            }
          });
        });

        // Max length of template name is 31 characters
        let adjustedTemplateName = (
          projectNames ? `${templateName}-${projectNames}` : templateName
        ).slice(0, 31);

        // Apparently we have duplicate template names in the database, so we'll append a number to the end of the name
        if (existingTemplateNames.has(adjustedTemplateName)) {
          // Max length of template name is 31 characters, and we add 4 characters for the number
          adjustedTemplateName = adjustedTemplateName.slice(0, 27);

          let i = 1;
          while (existingTemplateNames.has(`${adjustedTemplateName} (${i})`)) {
            i += 1;
          }
          adjustedTemplateName = `${adjustedTemplateName} (${i})`;
        }

        existingTemplateNames.add(adjustedTemplateName);

        acc[templateId] = {
          templateName: adjustedTemplateName,
          columns: relevantColumns,
          data: [],
          fieldTitleMap,
          mostRecentDateFieldIds: new Set(),
        };
      }

      const row = {
        ...form,
        ...parseFormResponseToReadableFormat({
          templateSections: templateSchema?.sections,
          responseSections: data?.sections,
          projectIdMap,
          settings,
        }),
      };

      if (form.mostRecentDates) {
        Object.entries(form.mostRecentDates).forEach(([fieldId, value]) => {
          row[fieldId] = value || '';
          acc[templateId].mostRecentDateFieldIds.add(fieldId);
        });
      }

      acc[templateId].data.push(row);
      return acc;
    }, {});

    const exportGroups = Object.values(groupedForms).map(({
      fieldTitleMap: ftMap,
      mostRecentDateFieldIds: fieldIds,
      ...group
    }) => {
      fieldIds.forEach((fieldId) => {
        const fieldTitle = ftMap[fieldId] || fieldId;
        group.columns.push({
          title: `${fieldTitle}: Most Recent Day Worked`,
          field: fieldId,
        });
      });
      return group;
    });

    exporter.createFormListExport(exportGroups);
    setExportLoading(false);
  }, [tableData, columns, filteredRows]);

  useEffect(() => {
    if (!registerFormHeaderAction || !hideExportButton) return undefined;
    registerFormHeaderAction(
      <OnTraccrButton
        title="Export"
        type="secondary"
        loading={exportLoading}
        onClick={onExport}
        icon={<DownloadOutlined className="forms-subpage-action-button-icon" style={{ marginRight: 0 }} />}
        className="forms-subpage-action-button"
        iconLeft
      />,
    );
    return () => registerFormHeaderAction(null);
  }, [registerFormHeaderAction, hideExportButton, onExport, exportLoading]);

  const onTableFilter = useCallback((_pagination, activeFilters, _sorter, extra) => {
    if (extra?.currentDataSource) {
      setFilteredRows(extra.currentDataSource);
      setFilters(activeFilters);
    }
  }, []);

  const onClearFilter = useCallback(() => {
    dispatch(updateFormFilters(null));
  }, []);

  return (
    <div>
      <Row className="forms-list-header" style={{ marginBottom: 14 }} align="middle" justify="space-between" gutter={20}>
        <Col>
          <Row align="middle" gutter={15}>
            {!customSearch && formList.length <= 1000 && (
              <Col>
                <Input.Search
                  type="search"
                  className="searchbar"
                  placeholder="Search"
                  allowClear
                  style={{ width: 350 }}
                  onChange={onSearch}
                  data-testid="formsLogSearch"
                />
              </Col>
            )}
            {customSearch && (
              <Col>
                { customSearch }
              </Col>
            )}
            {searchLoading && (
              <Col style={{ height: 20, width: 20 }}>
                <Spin size="small" />
              </Col>
            )}
            {option && (
              <Col style={{ marginLeft: 2 }}>
                <OnTraccrButton {...option} />
              </Col>
            )}
            {!hideExportButton && (
              <Col>
                <OnTraccrButton
                  title="Export"
                  loading={exportLoading}
                  onClick={onExport}
                />
              </Col>
            )}
            {enableFormFilters && (
              <>
                <Col>
                  <OnTraccrButton
                    title=""
                    type="secondary"
                    onClick={() => setShowFilterDrawer(true)}
                    icon={(
                      isFilterActive
                        ? <FilterFilled style={{ marginLeft: 0 }} />
                        : <FilterOutlined style={{ marginLeft: 0 }} />
                    )}
                    className="forms-subpage-action-button"
                  />
                </Col>
                {isFilterActive && (
                  <Col>
                    <OnTraccrButton
                      title="Clear Filter"
                      type="secondary"
                      onClick={onClearFilter}
                      className="forms-subpage-action-button"
                    />
                  </Col>
                )}
              </>
            )}
            {onAnalyticsClick && (
              <Col>
                <OnTraccrButton
                  title="Analytics"
                  onClick={onAnalyticsClick}
                />
              </Col>
            )}
          </Row>
        </Col>
        {!hideDate && (
          <Col style={{ display: 'flex', justifyContent: 'center' }}>
            <TimeCardDatePicker
              onChange={(newRange) => {
                if (newRange && newRange.length === 2) {
                  const [start, end] = newRange;
                  onRangeChange([
                    DateTime.fromMillis(start.valueOf()).startOf('day').toMillis(),
                    DateTime.fromMillis(end.valueOf()).endOf('day').toMillis(),
                  ]);
                }
              }}
              value={range && range.length === 2 ? [
                DateTime.fromMillis(range[0]),
                DateTime.fromMillis(range[1]),
              ] : null}
              type={calendarType}
              onTypeChange={setCalendarType}
            />
          </Col>
        )}
      </Row>
      {(!hideWhenEmpty || tableData.length > 0)
        && (
        <ResizeObserver onResize={onTableResize}>
          <div className="file-list-container forms-virtual-list-container" style={{ height }}>
            <Table
              scroll={{
                y: height,
              }}
              dataSource={tableData}
              columns={columns}
              pagination={false}
              size="small"
              rowClassName="file-table-row"
              rowKey="id"
              components={tableComponents}
              onChange={onTableFilter}
            />
          </div>
        </ResizeObserver>
        )}
      <FormDetailView
        visible={showDetail}
        onClose={clearFormSelection}
        userMap={userMap}
        poCloseMode={poCloseMode}
        setPoCloseMode={setPoCloseMode}
        shouldGetTemplates={shouldGetTemplates}
        onCardColumnClick={onCardColumnClick}
        zIndex={onCardColumnClick ? 999 : 1000}
      />
      <InvoiceAddDrawer
        formId={selectedFormId}
        visible={invDrawerVisible}
        mode={invDrawerMode}
        useRange={false}
        closeDrawer={closeDrawer}
        updateInvDrawerMode={updateInvDrawerMode}
      />
      <FormFilterDrawer
        visible={showFilterDrawer}
        onClose={() => setShowFilterDrawer(false)}
      />
      <FormResponder
        visible={showResponder}
        onClose={clearFormSelection}
        history={history}
        isCopy
      />
      {(loading || parentLoading)
        && (
          <Row justify="center" align="middle" className="form-loading-container">
            <Spin size="large" />
          </Row>
        )}
    </div>
  );
}

FormsList.propTypes = {
  enableFormFilters: PropTypes.bool,
  isFilterActive: PropTypes.bool,
  isPO: PropTypes.bool,
  skipLoad: PropTypes.bool,
  parentLoading: PropTypes.bool,
  shouldGetTemplates: PropTypes.bool,
  onCardColumnClick: PropTypes.func,
  onAnalyticsClick: PropTypes.func,
  hideExportButton: PropTypes.bool,
  shouldApplyRange: PropTypes.bool,
  registerFormHeaderAction: PropTypes.func,
};

FormsList.defaultProps = {
  enableFormFilters: false,
  isFilterActive: false,
  isPO: false,
  skipLoad: false,
  parentLoading: false,
  shouldGetTemplates: true,
  onCardColumnClick: null,
  onAnalyticsClick: null,
  hideExportButton: false,
  shouldApplyRange: false,
  registerFormHeaderAction: null,
};

export default FormsList;
